import React, { createContext, useEffect, useRef, useState } from "react";
import { ConfigProvider, Spin } from "antd";
import axios from "axios";
import AppRoutes from "./Routes/AppRoutes";
import GlobalLoader from "./Components/GlobalLoader";
import "./App.css";
import updateUserStates, {
  getUser,
  handleUpdateUser,
  getRefreshToken,
  handleLogout,
  setStoredToken,
} from "./Utlis/UpdateUserState";
import { refresh } from "./API/auth";
import PUBLIC_STANDALONE_PATHS from "./Utlis/PublicStandalonePaths";

const UserContext = createContext();

function App() {
  const [userData, setUserData] = useState({});
  const [userToken, setUserToken] = useState({ access: "", refresh: "" });
  const [loading, setLoading] = useState(true);
  const hasInitialized = useRef(false);
  const refreshPromiseRef = useRef(null);

  // 2026-09-18, direct report ("sometimes the endpoints return 401 and
  // sometimes the flow is accurate... revised application user can login
  // and get access to the revision endpoints and pages only, and also
  // they can come directly to the revised page from the email") -- root
  // cause: this file's own boot-time refresh (below) and its axios
  // interceptor both call redirectToLogin() UNCONDITIONALLY on any
  // refresh failure, regardless of which page the user is actually on.
  // AppRoutes/index.js already carves these exact three routes out as
  // reachable with no session at all (the emailed `?token=` link, or a
  // revision-requested user's own freshly-issued Bearer session) --
  // but nothing here knew that, so a stale/failed token sitting in
  // storage from a COMPLETELY UNRELATED earlier session could still
  // yank the user away from a page that never needed a valid session in
  // the first place. Skipping the hard redirect here (not the refresh
  // attempt itself, nor handleLogout's own local cleanup) means: the
  // `?token=` flow was never touched by any of this anyway (no auth
  // header at all), and the Bearer-session flow now correctly falls
  // through to the revise page's own try/catch (`setLoadError`) instead
  // of a full-app bounce to `/selection`.
  const isOnPublicStandalonePath = () =>
    PUBLIC_STANDALONE_PATHS.some((path) =>
      window.location.pathname.startsWith(path),
    );

  const redirectToLogin = () => {
    if (isOnPublicStandalonePath()) {
      return;
    }
    window.location.href = "/selection";
  };

  // 2026-09-18, same-day follow-up, direct report ("i submitted the
  // revised application, admin requested revision again, now when i try
  // to LOGIN the refresh token api gives 401 and i am unable to get the
  // application") -- a second, distinct half of the same root cause: the
  // BOOT-TIME refresh below (the one that runs on every fresh page load,
  // before the user has done anything at all) was ALSO calling
  // redirectToLogin() on failure -- and the path-based skip above only
  // covers the revise/qr-scan pages, not the login/2FA pages themselves.
  // A stale, already-rotated refresh token sitting in storage from an
  // EARLIER session (easy to accumulate across repeated
  // submit-revision -> admin-requests-revision-again cycles, each of
  // which mints its own fresh token pair) would 401 on this proactive
  // boot check and bounce the user to `/selection` the INSTANT they
  // loaded the login page to try again -- before they'd even entered
  // their credentials. There is no "current session" to protect at boot
  // time in the first place, so `redirectOnFailure` defaults to false
  // here: a failed boot-time refresh just quietly clears the dead
  // session (handleLogout) and lets whatever page the browser already
  // landed on render normally -- including a fresh login attempt. The
  // interceptor below (a call made *during* active use of an
  // already-loaded page) keeps redirecting on failure, since that IS a
  // real "your session just died" moment worth surfacing.
  const refreshAccessToken = (token, { redirectOnFailure = true } = {}) => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const doRefresh = async () => {
      try {
        let myRefreshToken = token?.refresh;
        if (!myRefreshToken) {
          myRefreshToken = getRefreshToken();
        }

        const res = await refresh(myRefreshToken);

        if (res?.status === 200) {
          setStoredToken({
            access: res?.data?.access,
            refresh: res?.data?.refresh,
          });

          updateUserStates(setUserData, setUserToken);

          return res?.data?.access;
        } else {
          handleLogout(setUserData, setUserToken);
          if (redirectOnFailure) {
            redirectToLogin();
          }
          return null;
        }
      } catch (error) {
        handleLogout(setUserData, setUserToken);
        if (redirectOnFailure) {
          redirectToLogin();
        }
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = doRefresh();
    return refreshPromiseRef.current;
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.code === "subscription_payment_required"
        ) {
          const storedUser = await getUser();
          if (storedUser && !storedUser.subscription_payment_required) {
            handleUpdateUser(setUserData, {
              ...storedUser,
              subscription_payment_required: true,
            });
          }
          if (window.location.pathname !== "/my-subscription") {
            window.location.replace("/my-subscription");
          }
          return Promise.reject(error);
        }

        if (
          error?.response?.status === 401 &&
          error.config.url !==
            `${process.env.REACT_APP_BACKEND_URL}auth/login/` &&
          error.config.url !==
            `${process.env.REACT_APP_BACKEND_URL}auth/refresh-token/`
        ) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            error.config.headers["Authorization"] = "Bearer " + newToken;
            return axios(error.config);
          } else {
            handleLogout(setUserData, setUserToken);
            redirectToLogin();
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeUserState = async () => {
      const updatedStates = updateUserStates(setUserData, setUserToken);
      if (updatedStates?.token?.access && updatedStates?.token?.refresh) {
        await refreshAccessToken(updatedStates.token, {
          redirectOnFailure: false,
        });
      }
      setLoading(false);
    };

    initializeUserState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#4928FD",
        },
      }}
    >
      <UserContext.Provider
        value={{ userData, setUserData, userToken, setUserToken }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "100%",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            <AppRoutes />
            <GlobalLoader />
          </>
        )}
      </UserContext.Provider>
    </ConfigProvider>
  );
}

export default App;
export { UserContext };
